import { useMemo } from "react";
import { toast } from "sonner";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { isApiError, useGroups, useMyGroups, useUpdateProject } from "@/lib/api";
import { useUser } from "@/lib/user-context";

// Sentinel Select value for "no group" (leaves the project ungrouped). Cannot
// collide with a real group id (ids are numeric). Matches the sentinel used
// by NewProjectDialog's create-time picker.
const NO_GROUP = "__none__";

function getApiErrorMessage(err: unknown): string {
  if (isApiError(err)) {
    const detail = (err.body as { detail?: unknown } | undefined)?.detail;
    if (typeof detail === "string") {
      return detail;
    }
  }
  return "Failed to update the project's group. Please try again.";
}

interface GroupSelectProps {
  projectId: number;
  currentGroupId: number | null;
  currentGroupName: string | null;
  /** Only the project owner or an admin may change the group -- read-only otherwise. */
  editable: boolean;
  className?: string;
}

/**
 * Group picker for an existing project (NOR-306-ish "change group after
 * creation"). Mirrors NewProjectDialog's create-time picker: admins may
 * assign any group, non-admin owners only groups they belong to -- the
 * backend enforces this too, and returns 403 for anything else.
 */
export function GroupSelect({
  projectId,
  currentGroupId,
  currentGroupName,
  editable,
  className,
}: GroupSelectProps) {
  const { user: viewer } = useUser();
  const isAdmin = viewer?.role === "admin";
  const { data: myGroups = [] } = useMyGroups();
  const { data: allGroups = [] } = useGroups(editable && isAdmin);
  const assignableGroups = isAdmin ? allGroups : myGroups;
  const updateProject = useUpdateProject();

  // Merge in the project's current group even if it's not one the viewer is
  // assignable to (e.g. an admin previously scoped it to a group this
  // non-admin owner doesn't belong to) -- otherwise the Select would show no
  // matching option for the project's actual current value.
  const options = useMemo(() => {
    const map = new Map<number, string>();
    for (const group of assignableGroups) {
      map.set(group.id, group.name);
    }
    if (currentGroupId != null && currentGroupName) {
      map.set(currentGroupId, currentGroupName);
    }
    return [...map.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [assignableGroups, currentGroupId, currentGroupName]);

  if (!editable) {
    return <span className={className}>{currentGroupName ?? "No group"}</span>;
  }

  const value = currentGroupId != null ? String(currentGroupId) : NO_GROUP;

  function handleChange(next: string) {
    const groupId = next === NO_GROUP ? null : Number(next);
    if (groupId === currentGroupId) {
      return;
    }
    updateProject.mutate(
      { projectId, groupId },
      {
        onSuccess: () => {
          toast.success(
            groupId == null ? "Project unassigned from group." : "Project group updated.",
          );
        },
        onError: (err) => {
          toast.error(getApiErrorMessage(err));
        },
      },
    );
  }

  return (
    <Select value={value} onValueChange={handleChange} disabled={updateProject.isPending}>
      <SelectTrigger className={className ?? "h-8 w-full sm:w-56"}>
        <SelectValue placeholder="No group" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={NO_GROUP}>No group</SelectItem>
        {options.map((group) => (
          <SelectItem key={group.id} value={String(group.id)}>
            {group.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
